import { useState, useRef, useEffect } from "react";
import {
  buttonStyles,
  secondaryButtonStyles,
  ButtonWrapper,
  MenuItemWrapper,
  MenuWrapper,
  SubmenuWrapper,
  IconButtonWrapper,
} from "./MoreButtonStyled";
import { useOutsideClick } from "../../utils/useOutsideClick"; // Import the hook
import { DotsThreeOutline } from "@phosphor-icons/react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import { DownIcon, UpIcon } from "../../assets/icons";
import NextIcon from "../../assets/images/next.svg";

const MoreButton = ({
  menuOptions,
  menuPosition,
  top,
  right,
  width,
  customwidth,
  buttonText,
  disabled,
  variant = "primary",
}) => {
  const isQuiet = variant === "secondary";
  const caretColor = isQuiet ? "#48566B" : "#FFFFFF";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [shareMenu, setShareMenu] = useState(false);
  const [menuPositionOffset, setMenuPositionOffset] = useState({
    top: top,
    bottom: undefined,
  });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useOutsideClick(menuRef, () => {
    setIsMenuOpen(false);
    setShareMenu(false);
  });

  // Preload menu icons so they appear instantly when the menu opens
  useEffect(() => {
    menuOptions?.forEach((option) => {
      if (option?.icon) {
        const img = new Image();
        img.src = option.icon;
      }
      option?.children?.forEach((child) => {
        if (child?.icon) {
          const img = new Image();
          img.src = child.icon;
        }
      });
    });
  }, [menuOptions]);

  const handleMenuClick = (e, option) => {
    e.stopPropagation();
    if (option?.disabled) return;

    if (!option.children) {
      option.onClick?.();
      setIsMenuOpen(false);
      setShareMenu(false);
    }
  };

  const handleSubmenuClick = (e, child) => {
    e.stopPropagation();
    child.onClick?.();
    setIsMenuOpen(false);
    setShareMenu(false);
  };

  return (
    <ButtonWrapper
      customwidth={customwidth}
      alignitem={buttonText ? "normal" : "center"}
      data-testid="button-wrapper"
    >
      {buttonText ? (
        <Button
          variant="contained"
          endIcon={
            isMenuOpen ? (
              <UpIcon color={caretColor} />
            ) : (
              <DownIcon color={caretColor} />
            )
          }
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
          sx={isQuiet ? secondaryButtonStyles : buttonStyles}
        >
          {buttonText}
        </Button>
      ) : (
        <IconButtonWrapper
          ref={buttonRef}
          onClick={(e) => {
            e.stopPropagation();
            const rect = buttonRef.current?.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 350 && menuOptions?.length > 2) {
              setMenuPositionOffset({ top: undefined, bottom: "20px" });
            } else {
              setMenuPositionOffset({ top: "20px", bottom: undefined });
            }
            setIsMenuOpen(!isMenuOpen);
          }}
        >
          <DotsThreeOutline
            size={16}
            weight="fill"
          />
        </IconButtonWrapper>
      )}

      {isMenuOpen && (
        <MenuWrapper
          ref={menuRef}
          data-testid="menu-wrapper"
          position={menuPosition}
          top={menuPositionOffset.top}
          bottom={menuPositionOffset.bottom}
          right={right}
          width={width}
        >
          {menuOptions?.map((option, index) => (
            <MenuItemWrapper
              key={option?.label}
              type={option?.label === "Share" ? "share" : undefined}
            >
              <div
                role="menuitem"
                data-testid={`menu-item-${option.label}`}
                tabIndex={0}
                className={`menu-item ${option?.disabled ? "disabled" : ""}`}
                onMouseEnter={() => setShareMenu(option?.label === "Share")}
                onClick={(e) => handleMenuClick(e, option)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleMenuClick(e, option);
                  }
                }}
              >
                {option.icon && <img src={option.icon} alt={option.label} />}
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {option.label}{" "}
                  {option?.label === "Share" && (
                    <img src={NextIcon} alt="next" />
                  )}
                </div>
              </div>

              {option.label === "Share" && shareMenu && (
                <SubmenuWrapper>
                  {option.children?.map((child) => (
                    <div
                      key={child?.label}
                      role="menuitem"
                      tabIndex={0}
                      className="menu-item"
                      onMouseEnter={() => setShareMenu(true)}
                      onClick={(e) => handleSubmenuClick(e, child)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSubmenuClick(e, child);
                        }
                      }}
                    >
                      {child?.icon && (
                        <img src={child?.icon} alt={child?.label} />
                      )}
                      {child?.label}
                    </div>
                  ))}
                </SubmenuWrapper>
              )}
            </MenuItemWrapper>
          ))}
        </MenuWrapper>
      )}
    </ButtonWrapper>
  );
};

export default MoreButton;
MoreButton.propTypes = {
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.string,
      isDanger: PropTypes.bool,
    })
  ).isRequired,
  menuPosition: PropTypes.string,
  top: PropTypes.string,
  right: PropTypes.string,
  width: PropTypes.string,
  customwidth: PropTypes.string,
  buttonText: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(["primary", "secondary"]),
};
